export const ORIGINAL_IMAGE_LOAD_TIMEOUT_MS = 30000;

const ORIGINAL_IMAGE_PARAMS_TO_REMOVE = [
  'lightweight', 'variant', 'scale', 'scale_width', 'scale_height',
  'format', 'quality', 'is_thumbnail',
  'width_to_fit', 'height_to_fit', 'align_to_fit',
  'width_to_fill', 'height_to_fill', 'align_to_fill',
];

export function getOriginalImageSrc(src) {
  if (!src) return null;

  const url = new URL(src, window.location.origin);
  if (url.pathname === '/share') {
    ORIGINAL_IMAGE_PARAMS_TO_REMOVE.forEach(param => url.searchParams.delete(param));
    url.searchParams.set('full_resolution', 'true');
  }
  return url.href;
}

function waitWithSignal(promise, signal) {
  let onAbort;
  return new Promise((resolve, reject) => {
    onAbort = () => reject(signal.reason || new DOMException('Image loading cancelled', 'AbortError'));
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(resolve, reject);
    if (signal.aborted) onAbort();
  }).finally(() => signal.removeEventListener('abort', onAbort));
}

export async function loadOriginalImageClipboardBlob(src, signal) {
  const response = await fetch(src, { credentials: 'same-origin', signal });
  if (!response.ok) throw new Error('Failed to load the original image');

  const blob = await response.blob();
  const imageUrl = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.src = imageUrl;
    // Decode PNGs too, so an error page with an image MIME type is rejected.
    await waitWithSignal(image.decode(), signal);
    const signature = new Uint8Array(await waitWithSignal(blob.slice(0, 8).arrayBuffer(), signal));
    const isPng = [137, 80, 78, 71, 13, 10, 26, 10].every((byte, index) => signature[index] === byte);
    if (blob.type === 'image/png' && isPng) return blob;

    // PNG is the interoperable clipboard format; retain source dimensions and alpha.
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Unable to prepare the image for copying');
    context.drawImage(image, 0, 0);

    return await waitWithSignal(new Promise((resolve, reject) => {
      canvas.toBlob(result => {
        if (result) resolve(result);
        else reject(new Error('Failed to prepare the image for copying'));
      }, 'image/png');
    }), signal);
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}
